// React
import { useState } from "react";
import { useNavigate } from "react-router-dom";

// Components
import {
  Dialog,
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Fade,
} from "@mui/material";
import { AppointmentStatusContent } from "../AppointmentStatusModal";
import { BookingInfo, PanAmNote } from "../Common";

// Styles
import { styles } from "./styles";
import { styles as modalStyles } from "../AppointmentStatusModal/styles";

// Resources
import { rescheduleAppointment } from "API/Resource/appointment";

// Libraries
import { useTranslation } from "react-i18next";

const EditAppointmentModal = ({
  patient,
  openModal,
  closeModal,
  newAppointment,
  currentAppointment,
  refreshAvailableSlots,
  refreshPatientAppointments,
  isMBS = false,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [openResponse, setOpenResponse] = useState(false);
  const [openSuccessScreen, setOpenSuccessScreen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorCode, setErrorCode] = useState("");

  const handleEdit = async () => {
    setLoading(true);
    try {
      await rescheduleAppointment(newAppointment, currentAppointment);
      setOpenSuccessScreen(true);
    } catch (error) {
      const errorMessages = {
        APPOINTMENT_SLOT_UNAVAILABLE: t(
          "editAppointmentModal.errorSlotUnavailable"
        ),
        APPOINTMENT_TIME_PASSED: t("editAppointmentModal.errorTimePassed"),
        APPOINTMENT_UPDATED: t("editAppointmentModal.errorUpdated"),
      };
      const defaultErrorMessage = t("editAppointmentModal.errorDefault");

      setErrorCode(error.message);
      const errorMessage = errorMessages[error.message] || defaultErrorMessage;
      setErrorMessage(errorMessage);
    } finally {
      setLoading(false);
      setOpenResponse(true);
    }
  };

  const handleOnClose = (_, reason) => {
    if (loading && reason === "backdropClick") {
      return;
    }

    closeModal();
  };

  const hasChangedTime =
    newAppointment.startTime !== currentAppointment.startTime;

  const hasChangedDate = newAppointment.date !== currentAppointment.date;

  const handleSuccessClose = async (_, reason) => {
    handleOnClose(_, reason);
    if (isMBS) {
      await onSuccess();
    } else {
      navigate("/");
    }
  };

  const handleRescheduleError = async () => {
    setLoading(true);
    try {
      if (errorCode === "APPOINTMENT_UPDATED" && refreshPatientAppointments) {
        await refreshPatientAppointments();
        return;
      }

      await refreshAvailableSlots();
    } finally {
      closeModal();
      setLoading(false);
    }
  };

  const PanAmNoteDisplay = () => {
    return <PanAmNote clinicId={newAppointment.clinicId} isMBS={isMBS} />;
  };

  const errorActionLabel =
    errorCode === "APPOINTMENT_UPDATED"
      ? t("editAppointmentModal.refreshPatientAppointments")
      : t("editAppointmentModal.refreshAvailableAppointments");

  return (
    <Dialog
      open={openModal}
      onClose={openSuccessScreen ? handleSuccessClose : handleOnClose}
      maxWidth={openResponse ? "sm" : "md"}
      fullWidth
      sx={{ minWidth: "300px" }}
      slots={{ transition: Fade }}
      slotProps={{
        transition: { timeout: { enter: 200, exit: 160 } },
        paper: openResponse
          ? {
              sx: modalStyles.dialogPaper(
                openSuccessScreen ? "success" : "error"
              ),
            }
          : undefined,
      }}
    >
      {!openResponse ? (
        <Stack spacing={1} sx={{ boxSizing: "border-box", p: 2 }}>
          <Typography
            color="primary"
            sx={{ fontWeight: "bold", fontSize: "1.25rem" }}
          >
            {t("editAppointmentModal.dialogTitle")}
          </Typography>
          <PanAmNoteDisplay />
          <Box sx={styles.appointmentComparison}>
            {/* Current Appointment */}
            <Stack spacing={2} sx={styles.appointmentBox}>
              <Typography variant="h6">
                {t("editAppointmentModal.currentAppointment")}
              </Typography>
              <BookingInfo
                patient={patient}
                appointment={{
                  ...currentAppointment,
                  clinicAddress: newAppointment.clinicAddress,
                  clinicName: newAppointment.clinicName,
                }}
              />
              <Box sx={styles.actionButtons}>
                <Button
                  onClick={closeModal}
                  variant="outlined"
                  disabled={loading}
                >
                  {t("editAppointmentModal.keepCurrent")}
                </Button>
              </Box>
            </Stack>

            {/* New Appointment */}
            <Stack spacing={2} sx={styles.appointmentBox}>
              <Typography variant="h6">
                {t("editAppointmentModal.newAppointment")}
              </Typography>
              <BookingInfo
                patient={patient}
                appointment={newAppointment}
                hasChangedTime={hasChangedTime}
                hasChangedDate={hasChangedDate}
              />
              <Box sx={styles.actionButtons}>
                <Button
                  onClick={handleEdit}
                  variant="contained"
                  disabled={loading}
                  startIcon={
                    loading && <CircularProgress size={14} color="inherit" />
                  }
                >
                  {loading
                    ? t("editAppointmentModal.rescheduling")
                    : t("editAppointmentModal.reschedule")}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Stack>
      ) : (
        <AppointmentStatusContent
          onClose={
            openSuccessScreen ? handleSuccessClose : handleRescheduleError
          }
          title={
            openSuccessScreen
              ? t("editAppointmentModal.successMessage")
              : t("errorDialog.title")
          }
          statusVariant={openSuccessScreen ? "success" : "error"}
          patient={patient}
          appointment={newAppointment}
          isMBS={isMBS}
          clinicMessage={<PanAmNoteDisplay />}
          statusMessage={openSuccessScreen ? undefined : errorMessage}
          showAppointmentSummary={openSuccessScreen}
          actions={
            openSuccessScreen
              ? []
              : [
                  {
                    label: errorActionLabel,
                    variant: "contained",
                    onClick: handleRescheduleError,
                    disabled: loading,
                    startIcon: loading && (
                      <CircularProgress size={18} color="inherit" />
                    ),
                  },
                ]
          }
        />
      )}
    </Dialog>
  );
};

export default EditAppointmentModal;
